/**
 * The single definition of what a role is and how to read one off an account.
 *
 * Deliberately free of any dependency (no `jsonwebtoken`, no zod) so it can be exported from the
 * browser entry point too — `auth/index.ts` cannot, which is why this lives in its own module.
 *
 * There is no `role` column on the account model: the database stores three booleans
 * (`superAdmin`/`admin`/`walker`) and the JWT carries the collapsed string. Both shapes reach
 * calling code, so everything here accepts either one.
 */
/** Every role, most privileged first. */
export const ROLES = ["superAdmin", "admin", "walker", "user"];
/** Back-office roles. Everything else is an external, self-registering company. */
export const STAFF_ROLES = ["superAdmin", "admin", "walker"];
const isRole = (value) => typeof value === "string" && ROLES.includes(value);
/**
 * Collapses an account (or JWT payload) to its single effective role.
 *
 * Boolean columns win over the token string: they are the database truth, and a token minted
 * before a role changed would otherwise keep reporting the stale value.
 *
 * @param user - An account row, a JWT payload, or nothing.
 * @returns The effective role; `"user"` when nothing indicates otherwise.
 */
export function roleFor(user) {
    if (!user)
        return "user";
    if (user.superAdmin === true)
        return "superAdmin";
    if (user.admin === true)
        return "admin";
    if (user.walker === true)
        return "walker";
    return isRole(user.role) ? user.role : "user";
}
/**
 * Whether an account satisfies a role requirement. `superAdmin` passes every check — the one
 * bypass rule, defined here so middleware, services and the SPA cannot disagree about it.
 *
 * @param user - An account row or JWT payload.
 * @param allowed - Role(s) that satisfy the requirement. Empty means "any authenticated role".
 */
export function hasRole(user, allowed = []) {
    const role = roleFor(user);
    if (role === "superAdmin")
        return true;
    const required = Array.isArray(allowed) ? allowed : [allowed];
    return required.length === 0 || required.includes(role);
}
/** Whether the account is back-office (walker/admin/superAdmin) rather than an external company. */
export const isStaff = (user) => STAFF_ROLES.includes(roleFor(user));
/** Whether the account is a superAdmin. */
export const isSuperAdmin = (user) => roleFor(user) === "superAdmin";
